import "vuetify/styles";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import colors from "vuetify/util/colors";
import { VFileUpload } from "vuetify/labs/VFileUpload";
import { iconAliases, liveswitchIconSet } from "@/iconsets/liveswitch";
import DeviceUtility from "@/helpers/device";
import { buttonAliases, buttonDefaults } from "@/plugins/vuetifyButtons";

// Negative `viewportMargin` lets menus overlap the on-screen keyboard / safe area on
// confirmed mobile/tablet. Desktop and unclassified ("unknown") UAs omit it — otherwise
// dropdowns extend past the viewport edge (LSP-15608). Positive-match gating ensures
// headless/SSR/unrecognized UAs default to the safer in-viewport behavior.
const { isMobile, isTablet } = DeviceUtility;
// Inlined from the retired shared presentation defaults (LSP-20796).
const formFieldDefaults = {
	variant: "outlined",
	hideDetails: "auto",
	rounded: "lg",
	color: "primary",
	autocomplete: "suppress"
};
export const menuProps = {
	maxWidth: "100%",
	...(isMobile || isTablet ? { viewportMargin: isMobile ? -250 : -150 } : {})
};

const vuetify = createVuetify({
	components: {
		...components,
		VFileUpload
	},
	directives,
	// <PrimaryBtn>, <SecondaryBtn>, <TertiaryBtn>, <DangerBtn>: the button roles (plugins/vuetifyButtons.ts).
	aliases: { ...buttonAliases },
	display: {
		mobileBreakpoint: "sm"
	},
	defaults: {
		VBtn: { elevation: 0 },
		...buttonDefaults,
		VCard: { elevation: 0 },
		global: {
			ripple: false
		},
		VTooltip: {
			disabled: DeviceUtility.isTouch,
			maxWidth: 300
		},
		VAvatar: {
			class: "team-avatar",
			variant: "tonal"
		},
		VSheet: {
			elevation: 0
		},
		// LSP-19495: validate on blur, never on the first keystroke. Vuetify 3's default is eager,
		// so typing one letter into Email turned the field red before the customer had finished
		// the address. Set here rather than per field so a new form inherits the rule and cannot
		// opt out by omission. Submit still validates everything: v-form.validate() ignores
		// validateOn. LSP-20847: "invalid-input", not bare "blur" — still blur-first, but once a
		// field is red it re-validates on every keystroke, so the error clears the moment the
		// value is fixed instead of waiting for the customer to click away.
		VTextField: {
			...formFieldDefaults,
			validateOn: "invalid-input"
		},
		VSwitch: {
			inset: true,
			variant: "outlined",
			hideDetails: "auto",
			color: "primary"
		},
		VSelect: {
			...formFieldDefaults,
			validateOn: "invalid-input",
			menuProps: menuProps
		},
		VAutocomplete: {
			...formFieldDefaults,
			validateOn: "invalid-input",
			menuProps: menuProps
		},
		VCombobox: {
			...formFieldDefaults,
			validateOn: "invalid-input",
			menuProps: menuProps
		},
		VTextarea: {
			...formFieldDefaults,
			maxHeight: 300,
			validateOn: "invalid-input"
		},
		VExpansionPanels: {
			elevation: 0
		},
		VMenu: {
			elevation: 8,
			rounded: "lg"
		},
		// LSP-16024: a visible snackbar must never swallow browser back/forward.
		//
		// VOverlay defaults closeOnBack to true, which registers a global router
		// beforeEach guard that CANCELS the navigation (returns false) so the back
		// gesture closes the overlay instead. VSnackbar then hardcodes
		// `persistent: true` on that inner VOverlay, and the closeOnBack callback
		// treats persistent as "do not close me" — it calls animateClick() (itself a
		// no-op, because VSnackbar also hardcodes noClickAnimation) and STILL returns
		// false. Nothing changes, so the next back press hits the identical state:
		// every back/forward is swallowed, silently, for as long as the snackbar is on
		// screen. vue-router restores the URL after the abort, so the user is left on
		// the page they tried to leave with no feedback at all. The reported symptom
		// was the sticky (timeout -1) Podium "not connected yet" banner wedging
		// /settings/organization-details: back appeared dead, and because the route
		// never returned to `settings` the tile grid never came back either.
		//
		// Snackbars are non-modal notifications — they have no claim on the back
		// gesture, and (being permanently persistent) can never consume it usefully.
		// Set globally rather than per-usage so the nine existing snackbars and any
		// future one are covered by construction.
		VSnackbar: {
			closeOnBack: false
		},
		VPagination: {
			VBtn: {
				elevation: 0,
				variant: "text",
				color: "black"
			}
		},
		VForm: {
			class: "d-flex flex-column ga-3"
		}
	},
	theme: {
		defaultTheme: "light",
		themes: {
			light: {
				colors: {
					// One blue: primary is the logo blue (it replaced #2d74ff). Every color="primary" button,
					// link, focus ring and rgb(var(--v-theme-primary)) style follows it.
					primary: "#0070FF",
					// Logo colors: the blue and the dark text of every LiveSwitch Contact logo. Styles use
					// rgb(var(--v-theme-brand)) / rgb(var(--v-theme-ink)) instead of the hex values.
					brand: "#0070FF",
					ink: "#03011D",
					secondary: colors.grey.darken3,
					accent: "#cad7f8",
					error: colors.red.accent3,
					background: "#f5f5f5",
					editorMenu: "#FFFFFF",
					editorMenuButtonBorder: "#000000",
					tabBg: "#f4f4f4",
					// initiator colors
					"schedule-fab-icon": "#099b9b",
					"request-recording-fab-icon": "#875efa",
					"spark-purple": "#875efa",
					"live-video-icon": "#ff8a00",
					"uploads-icon": "#9E9E9E",
					"photos-icon": "#0070ff",
					"recording-icon": "#ff460b",
					"form-icon": "#0070ff"
				}
			}
		}
	},
	icons: {
		defaultSet: "liveswitchIconSet",
		aliases: iconAliases,
		sets: {
			liveswitchIconSet
		}
	}
});

export default vuetify;
